import React from 'react';

const COLORS = {
  emerald:  { from: '#10B981', to: '#064E3B' },
  amber:    { from: '#FBBF24', to: '#B45309' },
  sky:      { from: '#38BDF8', to: '#0C4A6E' },
  violet:   { from: '#A78BFA', to: '#4C1D95' },
  rose:     { from: '#FB7185', to: '#9F1239' },
  indigo:   { from: '#818CF8', to: '#312E81' },
  teal:     { from: '#2DD4BF', to: '#134E4A' },
  fuchsia:  { from: '#E879F9', to: '#86198F' },
  orange:   { from: '#FB923C', to: '#9A3412' },
  lime:     { from: '#A3E635', to: '#3F6212' },
  cyan:     { from: '#22D3EE', to: '#155E75' },
  pink:     { from: '#F472B6', to: '#9D174D' },
};

const RING_COLORS = [
  '#9CA3AF', // 1 - gray
  '#22C55E', // 2 - emerald
  '#3B82F6', // 3 - blue
  '#A855F7', // 4 - purple
  '#F59E0B', // 5 - amber
  '#EF4444', // 6 - red
];

const initialsFrom = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Avatar circular con iniciales sobre gradiente + anillo de nivel + badge de puntos.
 * Props:
 *  - name: nombre del usuario (para iniciales)
 *  - color: clave de COLORS (emerald, amber, etc.)
 *  - level: número de nivel (1-6)
 *  - points: número entero opcional (para mostrar badge)
 *  - size: 'sm' | 'md' | 'lg' | 'xl'
 *  - showPoints: bool — muestra la chip de puntos sobre el avatar
 */
const LevelAvatar = ({
  name = '',
  color = 'emerald',
  avatarUrl,
  level = 1,
  points,
  size = 'md',
  showPoints = false,
  className = '',
}) => {
  const grad = COLORS[color] || COLORS.emerald;
  const ringColor = RING_COLORS[Math.min(Math.max(level - 1, 0), RING_COLORS.length - 1)];
  const [imgError, setImgError] = React.useState(false);
  // Reset error when avatarUrl prop changes
  React.useEffect(() => { setImgError(false); }, [avatarUrl]);
  const showImage = !!avatarUrl && !imgError;

  const sizeMap = {
    xs: { box: 28, text: 'text-[10px]', ring: 2,   badge: 'text-[8px] min-w-[14px] h-[14px] px-[3px]' },
    sm: { box: 36, text: 'text-xs',     ring: 2,   badge: 'text-[9px] min-w-[16px] h-[16px] px-1' },
    md: { box: 48, text: 'text-sm',     ring: 2.5, badge: 'text-[9px] min-w-[18px] h-[18px] px-1' },
    lg: { box: 72, text: 'text-xl',     ring: 3,   badge: 'text-[10px] min-w-[22px] h-[22px] px-1.5' },
    xl: { box: 112, text: 'text-3xl',   ring: 4,   badge: 'text-xs min-w-[28px] h-[28px] px-1.5' },
  };
  const s = sizeMap[size] || sizeMap.md;

  return (
    <div
      className={`relative inline-flex shrink-0 ${className}`}
      style={{ width: s.box, height: s.box }}
      data-testid="level-avatar"
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: ringColor,
          padding: s.ring,
        }}
      >
        <div
          className={`relative w-full h-full rounded-full overflow-hidden flex items-center justify-center font-bold text-white ${s.text}`}
          style={{
            background: `linear-gradient(135deg, ${grad.from} 0%, ${grad.to} 100%)`,
          }}
        >
          {/* Initials always rendered as the safe fallback */}
          <span aria-hidden={showImage}>{initialsFrom(name)}</span>
          {/* Photo overlays on top of initials when available */}
          {showImage && (
            <img
              src={avatarUrl}
              alt={name}
              loading="lazy"
              onError={() => setImgError(true)}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </div>
      </div>
      {/* Level chip bottom-right (sits half-outside the avatar) */}
      <div
        className={`absolute -bottom-1 -right-1 rounded-full bg-white shadow-md font-bold flex items-center justify-center leading-none ${s.badge}`}
        style={{ color: ringColor, border: `1.5px solid ${ringColor}` }}
        title={`Nivel ${level}`}
      >
        L{level}
      </div>
      {/* Optional points badge above */}
      {showPoints && typeof points === 'number' && (
        <div
          className="absolute -top-1 -left-1 rounded-full bg-[#00CC66] text-white font-bold text-[10px] px-1.5 py-0.5 shadow"
          title={`${points} PlanPoints`}
        >
          {points}
        </div>
      )}
    </div>
  );
};

export default LevelAvatar;
